import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { errorMessage } from '../../api/client';
import { E164, useAuthRouteState } from '../../auth/route-state';
import { Alert, Button, TextField } from '../../components/ui';

/**
 * Passwordless sign-in: the API texts a one-time code to a verified phone
 * number and the code is entered on the challenge page (SMS_OTP).
 */
export function PhoneSignIn() {
  const navigate = useNavigate();
  const { from } = useAuthRouteState();
  const [phone, setPhone] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: async () => {
      const value = phone.replace(/[\s()-]/g, '');
      if (!E164.test(value)) {
        setFieldError('Enter the number in international format, e.g. +14155552671.');
        return;
      }
      setFieldError(null);
      const challenge = await authApi.loginWithPhone(value);
      navigate('/sign-in/challenge', { state: { challenge, from } });
    },
  });

  return (
    <>
      <h1 className="auth__title">Sign in with phone</h1>
      <p className="auth__lead">
        We will text a one-time code to the verified phone number on your account.
      </p>
      {login.error ? <Alert kind="error">{errorMessage(login.error)}</Alert> : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          login.mutate();
        }}
        noValidate
      >
        <TextField
          label="Phone number"
          name="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+14155552671"
          autoFocus
          required
          value={phone}
          error={fieldError}
          hint="International format with the country code."
          onChange={(e) => setPhone(e.target.value)}
        />
        <Button type="submit" loading={login.isPending} disabled={!phone}>
          Text me a code
        </Button>
      </form>
      <p className="auth__switch">
        <Link to="/sign-in">Sign in with a password instead</Link>
      </p>
    </>
  );
}
